import React, { useCallback, useEffect, useState } from 'react';
import type { CommissionOverviewRow, CommissionStatement } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { euro, monthLabel, parseEuro, euroInput, todayKey } from '../format';
import { Empty, ErrorBanner, Field, Modal, Panel, Stat } from '../ui';
import { DateInput } from '../pickers';
import { ask } from '../dialogs';

const dateIt = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('it-IT');

/** Provvigioni: maturate mese per mese, pagate e saldo. Il commerciale vede le sue, l'amministratore registra i pagamenti. */
export function CommissionsPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const isAdmin = ctx.me.user.role === 'admin';
  const [months, setMonths] = useState(12);
  const [userId, setUserId] = useState<number | null>(isAdmin ? null : ctx.me.user.id);
  const [overview, setOverview] = useState<CommissionOverviewRow[] | null>(null);
  const [st, setSt] = useState<CommissionStatement | null>(null);
  const [openMonth, setOpenMonth] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      if (isAdmin) {
        const ov = await api.get<CommissionOverviewRow[]>(`/api/commissions/overview?months=${months}`);
        setOverview(ov);
        setUserId((cur) => cur ?? ov[0]?.userId ?? null);
      }
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api, isAdmin, months]);

  const loadStatement = useCallback(async () => {
    if (userId == null) return;
    try {
      setSt(await api.get<CommissionStatement>(`/api/commissions/statement?months=${months}${isAdmin ? `&userId=${userId}` : ''}`));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api, isAdmin, months, userId]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    void loadStatement();
  }, [loadStatement]);

  const refresh = () => {
    void load();
    void loadStatement();
  };

  const removePayment = async (id: number) => {
    if (!(await ask('Eliminare questo pagamento registrato?'))) return;
    try {
      await api.del(`/api/commissions/payments/${id}`);
      refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="panels single">
      <Panel
        title="Provvigioni"
        subtitle={st ? `${isAdmin ? `Quanto spetta a ${st.userName}` : 'Quanto ti spetta'}: ultimi ${months} mesi` : undefined}
        right={
          <div className="row-actions" style={{ margin: 0 }}>
            {isAdmin && overview && (
              <select value={userId ?? ''} title="Di quale commerciale vedere le provvigioni" onChange={(e) => setUserId(Number(e.target.value))}>
                {overview.map((o) => (
                  <option key={o.userId} value={o.userId}>
                    {o.userName}
                  </option>
                ))}
              </select>
            )}
            <select value={months} title="Quanti mesi vedere" onChange={(e) => setMonths(Number(e.target.value))}>
              {[6, 12, 24, 36].map((n) => (
                <option key={n} value={n}>
                  {n} mesi
                </option>
              ))}
            </select>
            {isAdmin && st && (
              <button type="button" className="btn dark" title="Segna che hai pagato le provvigioni a questo commerciale" onClick={() => setPaying(true)}>
                <Icon name="plus" size={16} /> Registra pagamento
              </button>
            )}
          </div>
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {isAdmin && overview && overview.length === 0 && <Empty icon="users">Nessun commerciale attivo.</Empty>}
        {!st ? (
          !isAdmin || overview?.length ? <div className="muted small">Caricamento…</div> : null
        ) : (
          <>
            <div className="stats-row" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              <Stat value={euro(st.totals.maturedCents)} label="Maturate" />
              <Stat value={euro(st.totals.paidCents)} label="Pagate" />
              <Stat value={euro(st.totals.balanceCents)} label="Da pagare" navy />
            </div>

            <table className="table">
              <thead>
                <tr>
                  <th>Mese</th>
                  <th className="num">Maturate</th>
                  <th className="num">Pagate</th>
                  <th className="num">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {st.months
                  .filter((m) => m.maturedCents || m.paidCents)
                  .map((m) => (
                    <React.Fragment key={m.month}>
                      <tr className="clickable" onClick={() => setOpenMonth(openMonth === m.month ? null : m.month)}>
                        <td>
                          <Icon name={openMonth === m.month ? 'chevronDown' : 'chevronRight'} size={14} /> {monthLabel(m.month)}
                        </td>
                        <td className="num">{euro(m.maturedCents)}</td>
                        <td className="num">{euro(m.paidCents)}</td>
                        <td className="num">
                          <b>{euro(m.balanceCents)}</b>
                        </td>
                      </tr>
                      {openMonth === m.month &&
                        m.clients.map((c) => (
                          <tr key={c.clientId}>
                            <td className="muted small" style={{ paddingLeft: 30 }}>
                              {c.clientName}
                              {c.referralCents > 0 && c.managementCents > 0 ? ' (segnalazione + gestione)' : c.referralCents > 0 ? ' (segnalazione)' : ' (gestione)'}
                            </td>
                            <td className="num muted small">{euro(c.referralCents + c.managementCents)}</td>
                            <td />
                            <td />
                          </tr>
                        ))}
                    </React.Fragment>
                  ))}
              </tbody>
              <tfoot>
                <tr>
                  <th>Totale</th>
                  <th className="num">{euro(st.totals.maturedCents)}</th>
                  <th className="num">{euro(st.totals.paidCents)}</th>
                  <th className="num">{euro(st.totals.balanceCents)}</th>
                </tr>
              </tfoot>
            </table>
            {st.months.every((m) => !m.maturedCents && !m.paidCents) && <Empty icon="percent">Nessuna provvigione maturata in questo periodo.</Empty>}

            {st.payments.length > 0 && (
              <>
                <h3 style={{ margin: '22px 0 8px' }}>Pagamenti ricevuti</h3>
                <table className="table">
                  <tbody>
                    {st.payments.map((p) => (
                      <tr key={p.id}>
                        <td>{dateIt(p.paidAt)}</td>
                        <td className="muted">
                          {monthLabel(p.month)}
                          {p.note ? ` · ${p.note}` : ''}
                        </td>
                        <td className="num">{euro(p.amountCents)}</td>
                        {isAdmin && (
                          <td className="num">
                            <button type="button" className="btn ghost" onClick={() => void removePayment(p.id)} title="Elimina il pagamento registrato">
                              <Icon name="trash" size={14} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            <p className="muted small" style={{ marginTop: 14 }}>
              {isAdmin
                ? 'Maturano sulle ore lavorate per i clienti del commerciale (segnalazione e gestione).'
                : 'Maturano sulle ore lavorate per i tuoi clienti (segnalazione e gestione).'}
            </p>
          </>
        )}
      </Panel>

      {paying && st && (
        <PaymentModal
          ctx={ctx}
          st={st}
          onClose={() => setPaying(false)}
          onSaved={() => {
            setPaying(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function PaymentModal({ ctx, st, onClose, onSaved }: { ctx: Ctx; st: CommissionStatement; onClose: () => void; onSaved: () => void }) {
  const open = [...st.months].reverse().find((m) => m.balanceCents > 0) ?? st.months[0];
  const [month, setMonth] = useState(open.month);
  const [amount, setAmount] = useState(euroInput(open.balanceCents > 0 ? open.balanceCents : 0));
  const [date, setDate] = useState(todayKey());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = (m: string) => {
    setMonth(m);
    const row = st.months.find((x) => x.month === m);
    if (row && row.balanceCents > 0) setAmount(euroInput(row.balanceCents));
  };

  const save = async () => {
    const cents = parseEuro(amount);
    if (!cents || cents <= 0) return setError('Indica un importo');
    setBusy(true);
    try {
      await ctx.api.post('/api/commissions/payments', { userId: st.userId, month, amountCents: cents, paidAt: date, note: note.trim() || null });
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Pagamento a ${st.userName}`}
      onClose={onClose}
      narrow
      footer={
        <div className="quote-actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={() => void save()} disabled={busy}>
            {busy ? 'Salvataggio…' : 'Registra'}
          </button>
        </div>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <Field label="Mese di competenza">
        <select value={month} onChange={(e) => pick(e.target.value)}>
          {st.months.map((m) => (
            <option key={m.month} value={m.month}>
              {monthLabel(m.month)} · saldo {euro(m.balanceCents)}
            </option>
          ))}
        </select>
      </Field>
      <div className="fields-2">
        <Field label="Importo (€)">
          <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
        </Field>
        <Field label="Data pagamento">
          <DateInput value={date} onChange={setDate} />
        </Field>
      </div>
      <Field label="Nota (facoltativa)">
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Bonifico, fattura n. …" />
      </Field>
    </Modal>
  );
}
